import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Tag, Plus, Trash2, Percent, DollarSign, Calendar, Hash, TicketPercent
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const AdminCoupons = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_discount: '',
    min_purchase: '',
    max_uses: '',
    expires_at: ''
  });

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchCoupons();
    const interval = setInterval(() => {
      fetchCoupons();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/coupons`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.code || formData.code.trim() === '') {
        alert('Informe o código do cupom');
        return;
      }
      if (!formData.discount_value || Number.isNaN(parseFloat(formData.discount_value))) {
        alert('Informe o valor do desconto');
        return;
      }
      const data = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        max_discount: formData.discount_type === 'percentage' && formData.max_discount !== ''
          ? parseFloat(formData.max_discount)
          : null,
        min_purchase: parseFloat(formData.min_purchase) || 0,
        max_uses: parseInt(formData.max_uses) || 0,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };

      await axios.post(`${API}/coupons`, data, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      setIsDialogOpen(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error('Error creating coupon:', error);
      alert(error.response?.data?.detail || error.response?.data?.message || 'Erro ao criar cupom');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cupom?')) return;
    
    try {
      await axios.delete(`${API}/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      alert(error.response?.data?.detail || error.response?.data?.message || 'Erro ao excluir cupom');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      max_discount: '',
      min_purchase: '',
      max_uses: '',
      expires_at: ''
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sem limite';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const { darkMode, theme } = useAdminTheme();

  return (
    <AdminLayout title="Cupons de Desconto" subtitle="Gerencie os cupons promocionais">
      {/* Header Actions */}
      <div data-tour="coupons-header" className="flex justify-end mb-6">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" /> Novo Cupom
            </Button>
          </DialogTrigger>
          <DialogContent className={`${theme.card} border ${theme.text}`}>
            <DialogHeader>
              <DialogTitle className={theme.text}>Criar Novo Cupom</DialogTitle>
              <DialogDescription className={theme.textMuted}>
                Configure os detalhes do cupom de desconto
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className={theme.text}>Código do Cupom</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className={theme.input}
                  placeholder="Ex: DESCONTO10"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={theme.text}>Tipo de Desconto</Label>
                  <Select value={formData.discount_type} onValueChange={(v) => setFormData({...formData, discount_type: v})}>
                    <SelectTrigger className={theme.input}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={theme.dropdown}>
                      <SelectItem value="percentage">Porcentagem (%)</SelectItem>
                      <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={theme.text}>Valor do Desconto</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    className={theme.input}
                    placeholder={formData.discount_type === 'percentage' ? '10' : '50.00'}
                    required
                  />
                </div>
              </div>

              {formData.discount_type === 'percentage' && (
                <div className="space-y-2">
                  <Label className={theme.text}>Desconto máximo (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.max_discount}
                    onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                    className={theme.input}
                    placeholder="Ex: 200.00"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={theme.text}>Compra Mínima (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.min_purchase}
                    onChange={(e) => setFormData({...formData, min_purchase: e.target.value})}
                    className={theme.input}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={theme.text}>Máximo de Usos</Label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                    className={theme.input}
                    placeholder="0 = ilimitado"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className={theme.text}>Data de Expiração</Label>
                <Input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                  className={theme.input}
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Criar Cupom
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Tag className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Total de Cupons</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{coupons.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <Percent className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Cupons Ativos</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{coupons.filter(c => c.active).length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <Hash className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Total de Usos</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{coupons.reduce((sum, c) => sum + c.uses_count, 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coupons Table */}
      <Card className={`${theme.card} border rounded-2xl`}>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-12">
              <Tag className={`h-12 w-12 ${theme.textMuted} mx-auto mb-4`} />
              <p className={theme.textMuted}>Nenhum cupom cadastrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={theme.tableRow}>
                  <TableHead className={theme.tableHead}>Código</TableHead>
                  <TableHead className={theme.tableHead}>Desconto</TableHead>
                  <TableHead className={theme.tableHead}>Mínimo</TableHead>
                  <TableHead className={theme.tableHead}>Usos</TableHead>
                  <TableHead className={theme.tableHead}>Expira em</TableHead>
                  <TableHead className={theme.tableHead}>Status</TableHead>
                  <TableHead className={theme.tableHead}>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map(coupon => (
                  <TableRow key={coupon.id} className={theme.tableRow}>
                    <TableCell className={`font-mono font-bold ${theme.text}`}>{coupon.code}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {coupon.discount_type === 'percentage' ? (
                          <>
                            <Percent className="h-4 w-4 text-blue-500" />
                            <span className="text-blue-600 font-semibold">{coupon.discount_value}%</span>
                          </>
                        ) : (
                          <>
                            <DollarSign className="h-4 w-4 text-emerald-500" />
                            <span className="text-emerald-600 font-semibold">{formatCurrency(coupon.discount_value)}</span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={theme.textSecondary}>
                      {coupon.min_purchase > 0 ? formatCurrency(coupon.min_purchase) : '-'}
                    </TableCell>
                    <TableCell className={theme.textSecondary}>
                      {coupon.uses_count} / {coupon.max_uses > 0 ? coupon.max_uses : '∞'}
                    </TableCell>
                    <TableCell>
                      <div className={`flex items-center space-x-2 ${theme.textSecondary}`}>
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(coupon.expires_at)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={coupon.active ? 'bg-emerald-500 text-white' : 'bg-gray-400 text-white'}>
                        {coupon.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleDelete(coupon.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};
